// Add Members Modal Component

import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Team, User, TeamMember } from '@/types/teams';
import { useToast } from '@/hooks/use-toast';

interface AddMembersModalProps {
  readonly team: Team;
  readonly currentMembers: User[];
  readonly allUsers: User[];
  readonly onClose: () => void;
  readonly onSave: () => void;
  readonly onAddMembers?: (teamId: string, members: Array<Omit<TeamMember, 'assignedAt' | 'assignedBy'>>) => Promise<TeamMember[]>;
}

export function AddMembersModal({
  team,
  currentMembers,
  allUsers,
  onClose,
  onSave,
  onAddMembers,
}: Readonly<AddMembersModalProps>) {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Get users not already in this team
  const availableUsers = useMemo(() => {
    const currentMemberIds = currentMembers.map(m => m.id);
    return allUsers.filter(u => !currentMemberIds.includes(u.id));
  }, [allUsers, currentMembers]);

  // Filter users by search
  const filteredUsers = useMemo(() => {
    if (!searchQuery) return availableUsers;
    const query = searchQuery.toLowerCase();
    return availableUsers.filter(
      u =>
        u.name.toLowerCase().includes(query) ||
        u.email.toLowerCase().includes(query)
    );
  }, [availableUsers, searchQuery]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUserIds(filteredUsers.map(u => u.id));
    } else {
      setSelectedUserIds([]);
    }
  };

  const handleSelectUser = (userId: string, checked: boolean) => {
    if (checked) {
      setSelectedUserIds([...selectedUserIds, userId]);
    } else {
      setSelectedUserIds(selectedUserIds.filter(id => id !== userId));
    }
  };

  const handleSave = async () => {
    if (selectedUserIds.length === 0) {
      toast({
        title: 'No users selected',
        description: 'Please select at least one user to add',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);
    try {
      if (onAddMembers) {
        // Use the provided callback to add members via API
        const membersToAdd = selectedUserIds.map(userId => {
          const user = allUsers.find(u => u.id === userId);
          return {
            userId,
            userName: user?.name ?? '',
            userEmail: user?.email ?? '',
            role: 'member',
          };
        });
        await onAddMembers(team.id, membersToAdd);
      }

      toast({
        title: 'Success',
        description: `${selectedUserIds.length} ${selectedUserIds.length === 1 ? 'user' : 'users'} added to ${team.name}`,
      });

      onSave();
    } catch (error) {
      console.error('Failed to add members:', error);
      toast({
        title: 'Error',
        description: 'Failed to add members. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Add Members to {team.name}</DialogTitle>
          <DialogDescription>
            Select users to add to this team
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 flex flex-col space-y-4 min-h-0 overflow-hidden">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search users by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Selected Count */}
          {selectedUserIds.length > 0 && (
            <div className="flex items-center gap-2">
              <Badge variant="secondary">
                {selectedUserIds.length} {selectedUserIds.length === 1 ? 'user' : 'users'} selected
              </Badge>
            </div>
          )}

          {/* Users Table */}
          <div className="flex-1 border rounded-lg min-h-0 overflow-auto" style={{ maxHeight: '350px' }}>
            <Table>
              <TableHeader className="sticky top-0 bg-muted z-10">
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={
                        selectedUserIds.length === filteredUsers.length &&
                        filteredUsers.length > 0
                      }
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Current Teams</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="bg-white dark:bg-background">
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12 text-muted-foreground">
                      {availableUsers.length === 0
                        ? 'All users are already members of this team'
                        : `No users found matching "${searchQuery}"`}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedUserIds.includes(user.id)}
                          onCheckedChange={(checked) =>
                            handleSelectUser(user.id, checked as boolean)
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div
                            className={cn(
                              'h-2 w-2 rounded-full',
                              user.status === 'active' ? 'bg-green-500' : 'bg-gray-400'
                            )}
                          />
                          <span className="font-medium">{user.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {user.email}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={user.status === 'active' ? 'default' : 'secondary'}
                        >
                          {user.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {user.teams.length} {user.teams.length === 1 ? 'team' : 'teams'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        <DialogFooter className="flex-shrink-0">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={selectedUserIds.length === 0 || isSaving}
          >
            {(() => {
              if (isSaving) return 'Adding...';
              const count = selectedUserIds.length;
              return `Add ${count} ${count === 1 ? 'User' : 'Users'}`;
            })()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
